import { PackageProviderName } from 'data/ppoints/constants';
import { useFirstSelectedProvider } from 'features/settings/components/ppoints/dashboard/usage-progress-card/hooks/useFirstSelectedProvider';
import useProviderLimitExceeded from 'hooks/plan/useProviderLimitExceeded';
import { useCallback } from 'react';

export interface ServiceProviderPlanLimit {
    /** Carrier limit of the current plan, `undefined` while loading or when the plan has no such limit */
    limit: number | undefined;
    /** Display name of the current plan */
    planName: string | undefined;
    /**
     * Whether saving the given selection would keep the shop within its plan's carrier limit.
     * Resolves to `true` while the plan is still loading or when it has no limit.
     */
    isWithinLimit: (selectedProviders: PackageProviderName[] | undefined) => boolean;
}

/**
 * Plan carrier limit check for the wizard's provider selection. Saving merges the selection into the
 * carriers already active in other countries, so the number compared to the limit is the distinct union
 * of both — the same number `useActivateProviders` checks on submit.
 */
export const useServiceProviderPlanLimit = (): ServiceProviderPlanLimit => {
    const { limit, planName, isLoading } = useProviderLimitExceeded();
    const { providers: activeProviders } = useFirstSelectedProvider();

    const isWithinLimit = useCallback(
        (selectedProviders: PackageProviderName[] | undefined) => {
            if (isLoading || limit === undefined) return true;
            return new Set([...activeProviders, ...(selectedProviders ?? [])]).size <= limit;
        },
        [isLoading, limit, activeProviders]
    );

    return { limit, planName, isWithinLimit };
};
